import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import PortfolioImage from "./PortfolioImage";

const NUDGE_PX = 320;
const NUDGE_PAUSE_MS = 1500;

const SPEED_PX_PER_FRAME = 0.6;

// Continuous horizontal auto-scroll for banners/designs, kept separate from
// regular Project cards. Real native scrolling (not a CSS transform loop),
// so it behaves like an ordinary horizontal scroller: a light constant
// auto-scroll via requestAnimationFrame, paused on desktop hover and paused
// while the user is actively touching/dragging on mobile so a swipe never
// fights the animation. Images render with object-contain at their own
// aspect ratio — never cropped or stretched to force a uniform box.
// With `renderItem`, each item renders as that card (e.g. the page's own
// project card) instead of the default image tile + lightbox.
const AutoScrollGallery = ({ items = [], renderItem }) => {
    const scrollRef = useRef(null);
    const [paused, setPaused] = useState(false);
    const [lightboxItem, setLightboxItem] = useState(null);
    const loop = items.length > 1 ? [...items, ...items] : items;

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || items.length < 2) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        // Track position as a float: browsers round scrollLeft, so adding a
        // sub-pixel step straight to it can stall on high-DPI screens.
        let raf;
        let pos = el.scrollLeft;
        const step = () => {
            if (!paused) {
                pos += SPEED_PX_PER_FRAME;
                if (pos >= el.scrollWidth / 2) pos -= el.scrollWidth / 2;
                el.scrollLeft = pos;
            } else {
                pos = el.scrollLeft;
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [paused, items.length]);

    if (!items.length) return null;

    // Arrows nudge the real scrollLeft (same coordinate space the auto-scroll
    // loop uses) and pause auto-scroll briefly so the click actually reads
    // as a deliberate jump instead of being instantly overridden next frame.
    const nudge = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        setPaused(true);
        el.scrollBy({ left: dir * NUDGE_PX, behavior: "smooth" });
        window.clearTimeout(nudge._t);
        nudge._t = window.setTimeout(() => setPaused(false), NUDGE_PAUSE_MS);
    };

    return (
        <>
            <div className="relative">
                {items.length > 1 && (
                    <button
                        type="button"
                        onClick={() => nudge(-1)}
                        aria-label="السابق"
                        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white shadow-lg hover:shadow-xl transition-all hover:bg-primary-600 hover:text-white text-gray-700"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                )}
                {items.length > 1 && (
                    <button
                        type="button"
                        onClick={() => nudge(1)}
                        aria-label="التالي"
                        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white shadow-lg hover:shadow-xl transition-all hover:bg-primary-600 hover:text-white text-gray-700"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                )}
                <div
                    ref={scrollRef}
                    dir="ltr"
                    className="flex gap-3 overflow-x-auto scrollbar-hide"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onTouchStart={() => setPaused(true)}
                    onTouchEnd={() => setPaused(false)}
                >
                    {renderItem && loop.map((item, idx) => (
                        <div key={`${item.id}-${idx}`} dir="rtl" className="flex-none w-60 md:w-72">
                            {renderItem(item)}
                        </div>
                    ))}
                    {!renderItem && loop.map((item, idx) => (
                        <button
                            key={`${item.id}-${idx}`}
                            type="button"
                            onClick={() => setLightboxItem(item)}
                            className="group flex-none w-60 md:w-80 aspect-video rounded-2xl overflow-hidden border border-gray-100 bg-gray-50"
                        >
                            <PortfolioImage
                                src={item.thumbnail}
                                alt={item.name}
                                category={item.categoryLabel}
                                className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-300"
                            />
                        </button>
                    ))}
                </div>
            </div>
            <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>

            {lightboxItem && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    onClick={() => setLightboxItem(null)}
                >
                    <button
                        type="button"
                        onClick={() => setLightboxItem(null)}
                        className="absolute top-4 left-4 text-white/70 hover:text-white text-sm font-bold"
                    >
                        إغلاق ✕
                    </button>
                    <img
                        src={lightboxItem.thumbnail}
                        alt={lightboxItem.name}
                        className="max-w-full max-h-[85vh] rounded-xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </>
    );
};

export default AutoScrollGallery;
